import _ from "lodash";
import { Vector2 } from "three";

export interface SourceAngleConfig {
  innerConeAngleRadiansAtMinDistance: number;
  innerConeAngleRadiansAtMaxDistance: number;
  innerConeMaxDistance: number;
  outerConeAngleRadians: number;
  outerConeMinReduction: number;
  outerConeMaxReduction: number;
  innerConeMinAmplification: number;
  innerConeMaxAmplification: number;
  outerConeBufferForMinValue: number;
}

export interface TargetAngleConfig {
  innerConeAngleRadians: number;
  outerConeAngleRadians: number;
  outerConeMinReduction: number;
  outerConeMaxReduction: number;
  outerConeBufferForMinValue: number;
}

export interface PanningConfig {
  minValue: number;
  maxValue: number;
  minRange: number;
  maxRange: number;
}

export const DEFAULT_PANNING_CONFIG: PanningConfig = {
  minValue: 0.65,
  maxValue: 0.95,
  minRange: 3,
  maxRange: 20,
};

export interface LowPanConfig {
  minRange: number;
  maxRange: number;
  minValue: number;
  maxValue: number;
  valueAtDistance: Vector2[];
  source: SourceAngleConfig;
  target: TargetAngleConfig;
}

export const DEFAULT_LOWPASS_CONFIG: LowPanConfig = {
  minRange: 6,
  maxRange: 30,
  minValue: 40,
  maxValue: 24000,
  valueAtDistance: [new Vector2(6, 24000), new Vector2(10, 8100), new Vector2(13.5, 2700), new Vector2(17, 900), new Vector2(20.5, 300), new Vector2(24, 100), new Vector2(30, 40)],
  source: {
    innerConeAngleRadiansAtMinDistance: Math.PI/8,
    innerConeAngleRadiansAtMaxDistance: Math.PI/12,
    innerConeMaxDistance: 20,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 4,
    outerConeMaxReduction: 8,
    innerConeMinAmplification: 3,
    innerConeMaxAmplification: 7,
    outerConeBufferForMinValue: Math.PI/12,
  },
  target: {
    innerConeAngleRadians: Math.PI/3,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 1.5,
    outerConeMaxReduction: 3,
    outerConeBufferForMinValue: Math.PI/12,
  },
};

export interface AudioConfig {
  tableRadius: number;
  outerConeBufferForMinValue: number;
  outerConeAngleRadians: number;
  // lowPassValueAtDistance: Vector2[];
  source: {
    innerConeAngleRadiansRange: Vector2;
    innerConeMaxDistance: number;
    innerConeVolumeAmplificationRange: Vector2;
    innerConeLowPassAmplificationRange: Vector2;
    outerConeVolumeReductionRange: Vector2;
    outerConeLowPassReductionRange: Vector2;
  },
  target: {
    innerConeAngleRadians: number;
    innerConeMaxDistance: number;
    innerConeVolumeAmplificationRange: Vector2;
    outerConeVolumeReductionRange: Vector2;
    outerConeLowPassReductionRange: Vector2;
  },
}

export const LOWPASS_VALUE_AT_DISTANCE = [new Vector2(6.5, 24000), new Vector2(10, 8100), new Vector2(13.5, 2700), new Vector2(17, 900), new Vector2(20.5, 300), new Vector2(24, 100), new Vector2(30, 40)];

export const DEFAULT_AUDIO_CONFIG: AudioConfig = {
  tableRadius: 0,
  outerConeBufferForMinValue: Math.PI/12,
  outerConeAngleRadians: Math.PI * 2/3,
  // lowPassValueAtDistance: [new Vector2(6.5, 24000), new Vector2(10, 8100), new Vector2(13.5, 2700), new Vector2(17, 900), new Vector2(20.5, 300), new Vector2(24, 100), new Vector2(30, 40)],
  source: {
    innerConeAngleRadiansRange: new Vector2(Math.PI/8, Math.PI/12),
    innerConeMaxDistance: 20,
    innerConeVolumeAmplificationRange: new Vector2(0.15, 5.50),
    innerConeLowPassAmplificationRange: new Vector2(3, 7),
    outerConeVolumeReductionRange: new Vector2(0.55, 0.90),
    outerConeLowPassReductionRange: new Vector2(17, 30),
  },
  target: {
    innerConeAngleRadians: Math.PI/4,
    innerConeMaxDistance: 12,
    innerConeVolumeAmplificationRange: new Vector2(0.15, 0.55),
    outerConeVolumeReductionRange: new Vector2(0.10, 0.65),
    outerConeLowPassReductionRange: new Vector2(1.5, 3),
  },
}

export const innerConeAngleRadiansForDistance = (distance: number): number => -0.0069 * distance + 0.4;
export const innerConeVolumeAmplificationForDistance = (distance: number, maxDistance: number): number => 0.3 * Math.min(maxDistance, distance);
export const outerConeVolumeReductionForDistance = (distance: number): number => Math.min(0.95, 0.015 * distance + 0.60);

export const getAudioConfig = (tableRadius: number, numSeats: number): AudioConfig => {
  const distanceBetweenKAdjacentSeats = (k: number): number => 2 * tableRadius * Math.sin(k * Math.PI / numSeats);
  const cosineLawForTriangleAngle = (a: number, b: number, c: number): number => Math.acos(((a * a) + (b * b) - (c * c))/(2 * a * b));
  const angleBetweenAdjacentSeats = cosineLawForTriangleAngle( distanceBetweenKAdjacentSeats(1), distanceBetweenKAdjacentSeats(2), distanceBetweenKAdjacentSeats(1) );

  return {
    tableRadius,
    outerConeBufferForMinValue: Math.PI/12,
    outerConeAngleRadians: Math.PI * 2/3,
    // lowPassValueAtDistance: [new Vector2(6.5, 24000), new Vector2(10, 8100), new Vector2(13.5, 2700), new Vector2(17, 900), new Vector2(20.5, 300), new Vector2(24, 100), new Vector2(30, 40)],
    source: {
      innerConeAngleRadiansRange: new Vector2(angleBetweenAdjacentSeats, Math.min(angleBetweenAdjacentSeats, innerConeAngleRadiansForDistance(2 * tableRadius))),
      innerConeMaxDistance: 2 * tableRadius,
      innerConeVolumeAmplificationRange: new Vector2(innerConeVolumeAmplificationForDistance(distanceBetweenKAdjacentSeats(1), 2 * tableRadius), innerConeVolumeAmplificationForDistance(2 * tableRadius, 2 * tableRadius)),
      innerConeLowPassAmplificationRange: new Vector2(4, Math.max(8, 2 * tableRadius - 6.5)),
      outerConeVolumeReductionRange: new Vector2(outerConeVolumeReductionForDistance(distanceBetweenKAdjacentSeats(1)), outerConeVolumeReductionForDistance(2 * tableRadius)),
      outerConeLowPassReductionRange: new Vector2(17, 22),
    },
    target: {
      innerConeAngleRadians: Math.PI/3,
      innerConeMaxDistance: 2 * tableRadius,
      innerConeVolumeAmplificationRange: new Vector2(0.15, 0.55),
      outerConeVolumeReductionRange: new Vector2(0.10, 0.65),
      outerConeLowPassReductionRange: new Vector2(1.0, 3.0),
    },
  }
}

export const INVERTED_LOWPASS_CONFIG: LowPanConfig = {
  minRange: 1,
  maxRange: 200,
  minValue: 24000,
  maxValue: 40,
  valueAtDistance: [new Vector2(1, 40), new Vector2(10, 100), new Vector2(13.5, 300), new Vector2(17, 900), new Vector2(20.5, 2700), new Vector2(24, 8100), new Vector2(200, 24000)],
  source: {
    innerConeAngleRadiansAtMinDistance: Math.PI/4,
    innerConeAngleRadiansAtMaxDistance: Math.PI/9,
    innerConeMaxDistance: 20,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 0,
    outerConeMaxReduction: 0,
    innerConeMinAmplification: 0,
    innerConeMaxAmplification: 0,
    outerConeBufferForMinValue: Math.PI/12,
  },
  target: {
    innerConeAngleRadians: Math.PI/3,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 1.5,
    outerConeMaxReduction: 3,
    outerConeBufferForMinValue: Math.PI/12,
  },
};

export interface VolumeConfig {
  distanceMinRange: number;
  sourceAngleConfig: SourceAngleConfig;
  targetAngleConfig: TargetAngleConfig;
}
export const DEFAULT_VOLUME_CONFIG: VolumeConfig = {
  distanceMinRange: 1,
  sourceAngleConfig: {
    innerConeAngleRadiansAtMaxDistance: Math.PI/12,
    innerConeAngleRadiansAtMinDistance: Math.PI/8,
    outerConeAngleRadians: Math.PI/2,
    innerConeMaxDistance: 20,
    outerConeMinReduction: 0.55,
    outerConeMaxReduction: 0.90,
    innerConeMinAmplification: 0.15,
    innerConeMaxAmplification: 5.50,
    outerConeBufferForMinValue: Math.PI/12,
  },
  targetAngleConfig: {
    innerConeAngleRadians: Math.PI/4,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 0.10,
    outerConeMaxReduction: 0.65,
    outerConeBufferForMinValue: Math.PI/12,
  },
};

export const INVERTED_VOLUME_CONFIG: VolumeConfig = {
  distanceMinRange: 0,
  sourceAngleConfig: {
    innerConeAngleRadiansAtMaxDistance: Math.PI/12,
    innerConeAngleRadiansAtMinDistance: Math.PI/6,
    outerConeAngleRadians: Math.PI/2,
    innerConeMaxDistance: 30,
    outerConeMinReduction: 0,
    outerConeMaxReduction: 0,
    innerConeMinAmplification: 0.10,
    innerConeMaxAmplification: 0.35,
    outerConeBufferForMinValue: Math.PI/12,
  },
  targetAngleConfig: {
    innerConeAngleRadians: Math.PI/4,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 0.05,
    outerConeMaxReduction: 0.35,
    outerConeBufferForMinValue: Math.PI/12,
  },
};
